import { useSyncExternalStore } from "react";
import { getCurrentUser, hasAccount } from "./auth";
import { getFlag, RENDER_3D_FLAG } from "./flags";
import { useDesign } from "./store";
import { track } from "./telemetry";
import type { Render3dSource } from "./events";
import type { TourScript } from "./render3d/tour";
import type { Render3dFloorStrip } from "./render3d/types";
import type { Design, InteriorSegmentRef } from "./types";

/**
 * App-wide "open the 3D walk-through" requests. The Design and Walls views
 * call {@link requestRender3d}; `Render3DHost` in `App.tsx` renders whatever
 * is open. Walking needs no account and no plan; editing finishes inside the
 * walk-through asks for an account (`Render3DOverlay`). A first run can
 * register a {@link Render3dGateHook} to handle a click first (the 3D first
 * run's showcase tour).
 */

/** The wall the camera should face when the walk-through opens. */
export interface Render3dFacing {
  wallIndex: number | null;
  interior: InteriorSegmentRef | null;
}

export interface Render3dRequest {
  /** Increments per open so a re-open always remounts the overlay. */
  id: number;
  source: Render3dSource;
  facing: Render3dFacing | null;
  /** Render this design instead of the live store design (debug gallery). */
  design?: Design;
  /** Debug gallery: floors laid as strips across the room. */
  floorStrips?: readonly Render3dFloorStrip[];
  /** Play this camera tour once the room is ready, then hand over control. */
  tour?: TourScript;
  /** What to offer once the tour is over (the 3D first run's "your room next"). */
  next?: Render3dNext;
}

export interface Render3dNext {
  title: string;
  body: string;
  cta: string;
  /** Short label for the collapsed pill. */
  pill: string;
  onSelect: () => void;
}

export interface Render3dOpenOptions {
  design?: Design;
  floorStrips?: readonly Render3dFloorStrip[];
  tour?: TourScript;
  next?: Render3dNext;
}

/** What a click did. `example`: the 3D first run's showcase tour opened. */
export type Render3dOutcome = "opened" | "disabled" | "example";

/**
 * Handles a click before the walk-through opens on the current design, or
 * returns `null` to let it open. Registered by the 3D first run
 * (`render3dOnboarding.ts`) so this module doesn't depend on it.
 */
export type Render3dGateHook = (source: Render3dSource, facing: Render3dFacing | null) => Render3dOutcome | null;

let current: Render3dRequest | null = null;
let seq = 0;
let gateHook: Render3dGateHook | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function useRender3dRequest(): Render3dRequest | null {
  return useSyncExternalStore(subscribe, () => current, () => null);
}

export function getRender3dRequest(): Render3dRequest | null {
  return current;
}

/** True while the walk-through is open. The plan editor's global keyboard
 *  shortcuts check this so walking with WASD / arrows never edits the room. */
export function isRender3dOpen(): boolean {
  return current !== null;
}

export function closeRender3d(): void {
  if (!current) return;
  current = null;
  emit();
}

export function setRender3dGateHook(hook: Render3dGateHook | null): void {
  gateHook = hook;
}

/** Open without reporting it (the `?debug=render3d` review surface, the 3D first run's showcase). */
export function openRender3dUngated(
  source: Render3dSource,
  facing: Render3dFacing | null = null,
  opts: Render3dOpenOptions = {}
): void {
  current = {
    id: ++seq,
    source,
    facing,
    ...(opts.design ? { design: opts.design } : {}),
    ...(opts.floorStrips ? { floorStrips: opts.floorStrips } : {}),
    ...(opts.tour ? { tour: opts.tour } : {}),
    ...(opts.next ? { next: opts.next } : {}),
  };
  emit();
}

/**
 * Open on the current design and report it. `own_room` is false while the
 * canvas still holds a starter room a first run seeded; `pass` is true when
 * the visitor has no account.
 */
export function openRender3d(source: Render3dSource, facing: Render3dFacing | null): void {
  openRender3dUngated(source, facing);
  track("render_3d_opened", {
    source,
    own_room: !/^starter_/.test(useDesign.getState().design.room.id),
    pass: !hasAccount(getCurrentUser()),
  });
}

/**
 * The 3D Render button's click. Flag off → nothing; otherwise the
 * registered gate hook gets the first say, then the walk-through opens on
 * the current design for anyone, signed in or not.
 */
export function requestRender3d(source: Render3dSource, facing: Render3dFacing | null = null): Render3dOutcome {
  if (!getFlag(RENDER_3D_FLAG)) return "disabled";
  const handled = gateHook?.(source, facing) ?? null;
  if (handled) return handled;
  openRender3d(source, facing);
  return "opened";
}

/** Test hook. */
export function _resetRender3dRequestsForTests(): void {
  current = null;
  seq = 0;
  gateHook = null;
  listeners.clear();
}
